'use client';
import { useEffect, useRef, useState } from 'react';

// Types out `texts` one at a time, each character-by-character, only starting the next entry
// once the previous one finishes. `enabled` gates the whole sequence (e.g. wait for an upstream
// animation); `skip` renders every entry fully immediately (e.g. it already played once before).
export function useSequentialTypewriter(texts: string[], enabled: boolean, skip: boolean) {
  const textsRef = useRef(texts);
  textsRef.current = texts;

  const [typingIdx, setTypingIdx] = useState(-1);
  const [currentText, setCurrentText] = useState('');

  useEffect(() => {
    if (enabled && !skip) setTypingIdx(0);
  }, [enabled, skip]);

  useEffect(() => {
    if (typingIdx < 0 || typingIdx >= textsRef.current.length) return;
    const text = textsRef.current[typingIdx] ?? '';
    if (!text) { setTypingIdx(i => i + 1); return; }
    let i = 0;
    setCurrentText('');
    const tick = setInterval(() => {
      i += 2;
      setCurrentText(text.slice(0, Math.min(i, text.length)));
      if (i >= text.length) {
        clearInterval(tick);
        setCurrentText(text);
        setTypingIdx(idx => idx + 1);
      }
    }, 16);
    return () => clearInterval(tick);
  }, [typingIdx]);

  if (!enabled) return texts.map(() => '');
  if (skip) return texts;

  return texts.map((text, i) => {
    if (i < typingIdx) return text;
    if (i === typingIdx) return currentText;
    return '';
  });
}
